/**
 * Olfacy product page: gallery, variant switching (price / per-ml / EMI / availability / image / URL),
 * quantity, copy-to-clipboard coupons, tabs ↔ accordions, and a simple pincode delivery estimate.
 * Add to Cart is handled by olfacy-product-card.js (form[data-ocard-form]); Buy Now posts to checkout.
 */
const PIN_KEY = 'olfacy:pincode';

class OlfacyProduct extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.variants = this.readJSON('[data-variants]') || [];
    this.moneyFormat = this.dataset.moneyFormat || '₹{{amount}}';
    this.emiMax = Number(this.dataset.emiMax) || 0;
    this.desktop = window.matchMedia('(min-width: 990px)');

    this.setupGallery(signal);
    this.setupTabs(signal);

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-index]')) this.onOptionChange();
    }, { signal });

    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-qty-input]')) this.syncQty(Math.max(1, Number(e.target.value) || 1), e.target);
    }, { signal });

    this.addEventListener('click', (e) => {
      const qty = e.target.closest('[data-qty]');
      if (qty) this.stepQty(Number(qty.dataset.qty), qty);
      const copy = e.target.closest('[data-copy]');
      if (copy) this.copy(copy);
      if (e.target.closest('[data-read-more]')) { e.preventDefault(); this.openPanel(this.querySelector('[data-details]')?.dataset.panel, true); }
      if (e.target.closest('[data-zoom]')) this.openZoom();
      if (e.target.closest('[data-zoom-close]')) this.querySelector('[data-zoom-dialog]')?.close();
    }, { signal });

    this.setupPincode(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  readJSON(selector) {
    try { return JSON.parse(this.querySelector(selector)?.textContent || 'null'); } catch { return null; }
  }

  /* ---------- Money ---------- */
  money(cents) {
    const hasDecimals = cents % 100 !== 0;
    const amount = new Intl.NumberFormat('en-US', { minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
    return this.moneyFormat.replace(/\{\{\s*[a-z_]+\s*\}\}/, amount);
  }

  /* ---------- Gallery ---------- */
  setupGallery(signal) {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.thumb)), { signal }));
    let ticking = false;
    this.track?.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
        this.thumbs.forEach((t, j) => t.toggleAttribute('aria-current', j === i));
      });
    }, { passive: true, signal });
  }

  goTo(i) {
    const slide = this.slides[i];
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft });
    this.thumbs.forEach((t, j) => t.toggleAttribute('aria-current', j === i));
  }

  openZoom() {
    const i = Math.round((this.track?.scrollLeft || 0) / (this.track?.clientWidth || 1));
    const img = this.slides[i]?.querySelector('img');
    const dialog = this.querySelector('[data-zoom-dialog]');
    if (!img || !dialog) return;
    const zoom = dialog.querySelector('[data-zoom-img]');
    zoom.src = img.currentSrc || img.src;
    zoom.alt = img.alt;
    dialog.showModal();
  }

  /* ---------- Variants ---------- */
  onOptionChange() {
    const selected = [...this.querySelectorAll('fieldset.opd__option')].map((fs) => fs.querySelector('input:checked')?.value);
    const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    if (!variant) return this.setAvailable(false, 'Unavailable');
    this.variant = variant;

    this.querySelector('[data-variant-input]').value = variant.id;
    this.setText('[data-price]', this.money(variant.price));

    const ml = parseFloat((variant.title.match(/([\d.]+)\s*ml/i) || [])[1]);
    const unit = this.querySelector('[data-unit]');
    if (unit) {
      unit.hidden = !ml;
      if (ml) unit.textContent = `(${this.money(Math.round(variant.price / ml))}/ml)`;
    }
    this.setText('[data-volume]', ml ? `${ml} Millilitres` : variant.title);
    this.setText('[data-size]', variant.title);

    const onSale = variant.compare > variant.price;
    const row = this.querySelector('[data-compare-row]');
    if (row) row.hidden = !onSale;
    if (onSale) {
      this.setText('[data-compare]', this.money(variant.compare));
      this.setText('[data-off]', `-${Math.round(((variant.compare - variant.price) * 100) / variant.compare)}%`);
    }

    if (this.emiMax) this.setText('[data-emi]', this.money(Math.floor(variant.price / this.emiMax)));
    this.querySelectorAll('[data-emi-list] [data-months]').forEach((li) => {
      const strong = li.querySelector('strong');
      if (strong) strong.textContent = `${this.money(Math.floor(variant.price / Number(li.dataset.months)))}/mo`;
    });

    this.setAvailable(variant.available, variant.available ? 'Add to Cart' : 'Sold out');

    const pin = this.querySelector('[data-pincode]');
    if (pin) { pin.dataset.priceCents = variant.price; if (this.lastPin) this.checkPin(this.lastPin); }

    if (variant.media) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.media));
      if (i >= 0) this.goTo(i);
    }

    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }

  setText(selector, text) {
    this.querySelectorAll(selector).forEach((el) => (el.textContent = text));
  }

  setAvailable(available, label) {
    this.querySelectorAll('[data-atc], [data-buy-now]').forEach((b) => (b.disabled = !available));
    this.querySelectorAll('[data-atc] [data-ocard-label]').forEach((l) => (l.textContent = label));
  }

  // There can be two steppers (mobile next to variants, desktop in the buy box); keep them in sync
  stepQty(delta, source) {
    const inputs = [...this.querySelectorAll('[data-qty-input]')];
    const current = source?.closest('.opd__qty')?.querySelector('[data-qty-input]') || inputs[0];
    if (!current) return;
    this.syncQty(Math.max(1, (Number(current.value) || 1) + delta));
  }

  syncQty(value, except) {
    this.querySelectorAll('[data-qty-input]').forEach((input) => {
      if (input !== except) input.value = value;
    });
  }

  async copy(button) {
    const label = button.querySelector('[data-copy-label]');
    const code = button.dataset.copy;
    try {
      await navigator.clipboard.writeText(code);
      if (label) { label.textContent = 'Copied!'; setTimeout(() => (label.textContent = code), 1500); }
    } catch {}
  }

  /* ---------- Tabs (desktop) / accordions (mobile) ---------- */
  setupTabs(signal) {
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.tabButtons = [...this.querySelectorAll('[data-tab]')];
    this.tabButtons.forEach((b) => b.addEventListener('click', () => this.openPanel(b.dataset.tab), { signal }));
    const layout = () => {
      if (this.desktop.matches) {
        const current = this.tabButtons.find((b) => b.getAttribute('aria-selected') === 'true') || this.tabButtons[0];
        if (current) this.openPanel(current.dataset.tab);
      } else {
        // Mobile: each accordion follows its "Open by default on mobile" setting
        this.panels.forEach((p) => (p.open = p.dataset.openMobile === 'true'));
      }
    };
    layout();
    this.desktop.addEventListener('change', layout, { signal });
  }

  openPanel(name, scroll = false) {
    const panel = this.panels.find((p) => p.dataset.panel === name);
    if (!panel) return;
    if (this.desktop.matches) {
      this.panels.forEach((p) => (p.open = p === panel));
      this.tabButtons.forEach((b) => b.setAttribute('aria-selected', String(b.dataset.tab === name)));
    } else {
      panel.open = true;
    }
    if (scroll) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------- Pincode estimate ---------- */
  setupPincode(signal) {
    const form = this.querySelector('[data-pin-form]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.checkPin(this.querySelector('[data-pin-input]').value.trim());
    }, { signal });
    let saved = '';
    try { saved = localStorage.getItem(PIN_KEY) || ''; } catch {}
    if (/^\d{6}$/.test(saved)) {
      this.querySelector('[data-pin-input]').value = saved;
      this.checkPin(saved);
    }
  }

  checkPin(pin) {
    const box = this.querySelector('[data-pincode]');
    const result = this.querySelector('[data-pin-result]');
    const sub = this.querySelector('[data-pin-sub]');
    if (!box || !result) return;
    result.classList.remove('is-error');
    if (!/^[1-9]\d{5}$/.test(pin)) {
      result.textContent = 'Please enter a valid 6-digit pincode';
      result.classList.add('is-error');
      sub.textContent = '';
      return;
    }
    const blocked = (box.dataset.blocked || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (blocked.some((prefix) => pin.startsWith(prefix))) {
      result.textContent = 'Sorry, we don’t deliver to this pincode yet';
      result.classList.add('is-error');
      sub.textContent = '';
      return;
    }
    this.lastPin = pin;
    try { localStorage.setItem(PIN_KEY, pin); } catch {}

    const min = Number(box.dataset.minDays) || 2;
    const max = Number(box.dataset.maxDays) || 5;
    const date = new Date();
    let added = 0;
    while (added < max) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0) added++; // skip Sundays
    }
    const when = date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
    const freeOver = Number(box.dataset.freeOver) || 0;
    const free = freeOver === 0 || Number(box.dataset.priceCents) >= freeOver;
    result.textContent = `${free ? 'Free delivery' : 'Delivery'} by ${when}`;
    sub.textContent = `Usually delivers in ${min}-${max} days`;
  }
}

if (!customElements.get('olfacy-product')) {
  customElements.define('olfacy-product', OlfacyProduct);
}
